import React from 'react';
import { withStyles, makeStyles } from '@material-ui/styles';
import { Link as RouterLink } from 'react-router-dom';
import { Grid, Typography } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

import LocationCityOutlinedIcon from '@material-ui/icons/LocationCityOutlined';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
import WorkOutlineOutlinedIcon from '@material-ui/icons/WorkOutlineOutlined';
import CategoryOutlinedIcon from '@material-ui/icons/CategoryOutlined';
import SecurityOutlinedIcon from '@material-ui/icons/SecurityOutlined';
import MailOutlineOutlinedIcon from '@material-ui/icons/MailOutlineOutlined';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import StraightenOutlinedIcon from '@material-ui/icons/StraightenOutlined';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { TotalUsers, TotalProfessions, City, Security, AttendancePoint } from './components';

const styles = theme => ({
    root: {
        backgroundColor: '#F6F7FB',
        minHeight: '100vh',
        padding: theme.spacing(4)
    },
    inner: {
        maxWidth: 1320,
        margin: '0 auto'
    },
    section: {
        marginBottom: theme.spacing(6)
    },
    sectionHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2.5)
    },
    sectionIconBadge: {
        width: 40,
        height: 40,
        minWidth: 40,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(63, 81, 181, 0.1)',
        color: '#3f51b5'
    },
    sectionTitle: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
        color: '#111827'
    },
    cardsGrid: {
        marginTop: 0
    }
});

// Reusable "section header" — icon badge + title, used to open every block.
const useSectionHeaderStyles = makeStyles(() => ({
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 20
    },
    badge: {
        width: 40,
        height: 40,
        minWidth: 40,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(63, 81, 181, 0.1)',
        color: '#3f51b5'
    },
    title: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
        color: '#111827'
    }
}));

const SectionHeader = ({ icon, title }) => {
    const classes = useSectionHeaderStyles();
    return (
        <div className={classes.row}>
            <div className={classes.badge}>{icon}</div>
            <Typography variant="h4" className={classes.title}>
                {title}
            </Typography>
        </div>
    );
};

// Reusable action tile — replaces the old uppercase pill buttons with a
// card-style row that fits the same visual language as the data cards below.
const useTileStyles = makeStyles(() => ({
    tile: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '16px 20px',
        borderRadius: 12,
        border: '1px solid #E5E7EB',
        backgroundColor: '#fff',
        textDecoration: 'none',
        transition: 'all 0.15s ease',
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
        '&:hover': {
            boxShadow: '0 4px 12px rgba(63, 81, 181, 0.12)',
            transform: 'translateY(-2px)'
        }
    },
    icon: {
        width: 38,
        height: 38,
        minWidth: 38,
        borderRadius: 9,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(63, 81, 181, 0.1)',
        color: '#3f51b5'
    },
    label: {
        fontWeight: 600,
        color: '#111827',
        fontSize: '0.95rem',
        lineHeight: 1.3
    },
    chevron: {
        marginLeft: 'auto',
        color: '#9CA3AF'
    }
}));

const ActionTile = ({ icon, label, to }) => {
    const classes = useTileStyles();
    return (
        <Grid item xs={12} sm={6} md={4}>
            <RouterLink to={to} className={classes.tile}>
                <div className={classes.icon}>{icon}</div>
                <Typography className={classes.label}>{label}</Typography>
                <ChevronRightIcon fontSize="small" className={classes.chevron} />
            </RouterLink>
        </Grid>
    );
};

// ---- Loading placeholders ----
// Mirror the footprint of the real data cards (City, TotalUsers, ...) and
// action tiles so nothing jumps around once the fetch resolves.
const useCardSkeletonStyles = makeStyles(theme => ({
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 16,
        border: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.06)'
    },
    content: {
        flex: 1,
        padding: theme.spacing(3)
    },
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(2)
    },
    headerText: {
        flex: 1,
        minWidth: 0,
        marginRight: theme.spacing(2)
    },
    footer: {
        padding: theme.spacing(1.5, 3),
        borderTop: '1px solid #EEF0F4'
    }
}));

const CardSkeleton = ({ withNumber, lines = 2 }) => {
    const classes = useCardSkeletonStyles();
    return (
        <div className={classes.root}>
            <div className={classes.content}>
                <div className={classes.headerRow}>
                    <div className={classes.headerText}>
                        <Skeleton variant="text" width="55%" height={26} />
                        {withNumber && <Skeleton variant="text" width="35%" height={40} />}
                    </div>
                    <Skeleton variant="circle" width={44} height={44} />
                </div>
                {Array.from({ length: lines }).map((_, i) => (
                    <Skeleton
                        key={i}
                        variant="text"
                        width={i === lines - 1 ? '60%' : '90%'}
                        height={20}
                    />
                ))}
            </div>
            <div className={classes.footer}>
                <Skeleton variant="text" width="45%" height={20} />
            </div>
        </div>
    );
};

const useTileSkeletonStyles = makeStyles(() => ({
    tile: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '16px 20px',
        borderRadius: 12,
        border: '1px solid #E5E7EB',
        backgroundColor: '#fff'
    }
}));

const TileSkeleton = () => {
    const classes = useTileSkeletonStyles();
    return (
        <Grid item xs={12} sm={6} md={4}>
            <div className={classes.tile}>
                <Skeleton variant="circle" width={38} height={38} />
                <Skeleton variant="text" width="70%" height={22} />
            </div>
        </Grid>
    );
};

class Settings extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            ...props,
            loading: true,
            metiers: {
                last: {
                    label: '',
                    date: ''
                },
                number: 0
            },
            users: {
                last: {
                    username: '',
                    profil: ''
                },
                number: 0
            },
            city: {
                city: '',
                mail: '',
                adresse: '',
                codePostal: ''
            },
            attendancePoint: {
                records: 0
            },
            security: [],
            emailTemplates: []
        };
    }

    componentDidMount = () => {
        this.getData();
    };

    getData = () => {
        fetch(
            `https://www.${
                window.location.hostname.split('.')[1]
                    ? window.location.hostname.split('.')[0] === 'www'
                        ? window.location.hostname.split('.')[1]
                        : window.location.hostname.split('.')[0]
                    : 'dev'
            }.api.digitmarche.fr/api/home/settings`,
            {
                method: 'GET'
            }
        )
            .then(response => {
                return response.ok ? response.json() : false;
            })
            .then(data => {
                this.setState({
                    ...data
                });
            })
            .catch(
                error => {
                    console.error(error);
                    alert('Echec de la récupération des données');
                } // Handle the error response object
            )
            .finally(() => this.setState({ loading: false }));
    };

    render() {
        const {
            classes,
            loading,
            metiers,
            users,
            city,
            security,
            emailTemplates,
            attendancePoint
        } = this.state;

        return (
            <div className={classes.root}>
                <div className={classes.inner}>
                    {/* Ville */}
                    <div className={classes.section}>
                        <SectionHeader icon={<LocationCityOutlinedIcon />} title="Ville" />
                        <Grid container spacing={3}>
                            <Grid item md={6} sm={12} xs={12}>
                                {loading ? <CardSkeleton lines={2} /> : <City data={city} />}
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                {loading ? (
                                    <CardSkeleton withNumber lines={1} />
                                ) : (
                                    <TotalUsers data={users} />
                                )}
                            </Grid>
                        </Grid>
                    </div>

                    {/* Facturation */}
                    <div className={classes.section}>
                        <SectionHeader icon={<ReceiptOutlinedIcon />} title="Facturation" />
                        <Grid container spacing={2}>
                            {loading ? (
                                <>
                                    <TileSkeleton />
                                    <TileSkeleton />
                                    <TileSkeleton />
                                </>
                            ) : (
                                <>
                                    <ActionTile
                                        icon={<ReceiptOutlinedIcon fontSize="small" />}
                                        label="Configuration de la facturation"
                                        to="/settings/bill-config"
                                    />
                                    <ActionTile
                                        icon={<MailOutlineOutlinedIcon fontSize="small" />}
                                        label={`Configurer les modèles de mail (${emailTemplates.records})`}
                                        to="/settings/email-template"
                                    />
                                    <ActionTile
                                        icon={<AssignmentOutlinedIcon fontSize="small" />}
                                        label="Configurer les AOT"
                                        to="/settings/aot"
                                    />
                                </>
                            )}
                        </Grid>
                    </div>

                    {/* Métiers */}
                    <div className={classes.section}>
                        <SectionHeader icon={<WorkOutlineOutlinedIcon />} title="Métiers" />
                        <Grid container spacing={3}>
                            <Grid item md={6} sm={12} xs={12}>
                                {loading ? (
                                    <CardSkeleton withNumber lines={1} />
                                ) : (
                                    <TotalProfessions data={metiers} />
                                )}
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                {loading ? (
                                    <CardSkeleton withNumber lines={0} />
                                ) : (
                                    <AttendancePoint data={attendancePoint} />
                                )}
                            </Grid>
                        </Grid>
                    </div>

                    {/* Domaine */}
                    <div className={classes.section}>
                        <SectionHeader icon={<CategoryOutlinedIcon />} title="Domaine" />
                        <Grid container spacing={2}>
                            {loading ? (
                                <TileSkeleton />
                            ) : (
                                <ActionTile
                                    icon={<StraightenOutlinedIcon fontSize="small" />}
                                    label="Configurer les unités"
                                    to="/settings/unit"
                                />
                            )}
                        </Grid>
                    </div>

                    {/* Sécurité */}
                    <div className={classes.section}>
                        <SectionHeader icon={<SecurityOutlinedIcon />} title="Sécurité" />
                        <Grid container spacing={3}>
                            <Grid item md={6} sm={12} xs={12}>
                                {loading ? (
                                    <CardSkeleton lines={3} />
                                ) : (
                                    <Security data={security} />
                                )}
                            </Grid>
                        </Grid>
                    </div>
                </div>
            </div>
        );
    }
}

export default withStyles(styles)(Settings);
